import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'

import { randomIntFromInterval } from '~/utils/maths/random'

import { CookieProps } from './context'

// Timestamp 24h
const DAY_TIMESTAMP = 1000 * 60 * 60 * 24

export type ProductItem = {
  uid: string
  date: number
}

export type Lang = ProductItem[]

export function getRecentlyViewedProducts(ctx: CookieProps) {
  const { cookieKey } = ctx
  const cookie = Cookies.get(cookieKey)

  return cookie ? JSON.parse(cookie) : {}
}

export function getRecentlyViewedProductsFromLocale(
  locale: string,
  ctx: CookieProps,
) {
  return getRecentlyViewedProducts(ctx)[locale]
}

/**
 * Add DB new Item
 * @param uid
 * @param locale
 * @param DB
 */
export function addUID(uid: string, locale: string, DB: Lang[]) {
  const item: ProductItem = {
    uid,
    date: Date.now(),
  }

  DB[locale].push(item)

  return DB
}

/**
 * Remove DB Item
 * @param uid
 * @param locale
 * @param DB
 */
export function removeUID(uid: string, locale: string, DB: Lang[]) {
  DB[locale] = DB[locale].filter((n: ProductItem) => n.uid !== uid)
  return DB
}

/**
 * Update DB Existing Item date
 * @param uid
 * @param locale
 * @param DB
 */
export function updateUID(uid: string, locale: string, DB: Lang[]) {
  const now = Date.now()
  const index = DB[locale].findIndex((n: ProductItem) => n.uid === uid)
  DB[locale][index].date = now
}

export function addProductViewed(
  uid: string,
  locale: string,
  ctx: CookieProps,
) {
  const { expireDays, maxProducts, cookieAge, cookieKey } = ctx
  const DB = getRecentlyViewedProducts(ctx)

  if (!DB[locale]) DB[locale] = []

  if (DB[locale].findIndex((n: ProductItem) => n.uid === uid) === -1)
    addUID(uid, locale, DB)
  else updateUID(uid, locale, DB)

  // Filter too old items and delete it
  for (const [key, loc] of Object.entries<Lang>(DB)) {
    DB[key] = loc.filter((n) => {
      return Date.now() < n.date + expireDays * DAY_TIMESTAMP
    })
  }

  // Reorder
  for (const [, loc] of Object.entries<Lang>(DB)) {
    loc.sort(function (a, b) {
      return b.date - a.date
    })
  }

  // Delete older items if > MAX_PRODUCTS_RECENTLY_VIEWED
  for (const [, loc] of Object.entries<Lang>(DB)) {
    if (loc.length > maxProducts) {
      DB[locale] = loc.slice(0, maxProducts)
    }
  }

  Cookies.set(cookieKey, JSON.stringify(DB), {
    expires: cookieAge,
  })
}

// Fake UID set of datas
const RANDOMS_UID = [
  'bague-octogone-or-jaune',
  'bague-octogone-or-rose',
  'bague-octogone-or-blanc',
]

// Fake LOCALES
const LOCALES = ['fr-fr', 'en-us']

/**
 * Debug function to add random UID when traveling on website
 * @param ctx
 */
export function addRandomUID(ctx: CookieProps) {
  const UID = RANDOMS_UID[randomIntFromInterval(0, RANDOMS_UID.length - 1)]
  const locale = LOCALES[randomIntFromInterval(0, LOCALES.length - 1)]
  addProductViewed(UID, locale, ctx)
}

export default function useProductsRecentlyViewed(
  locale: string,
  ctx: CookieProps,
) {
  const [uids, setUids] = useState<string[]>([])

  useEffect(() => {
    if (locale && ctx) {
      const products = getRecentlyViewedProductsFromLocale(locale, ctx)
      if (products) setUids(products?.map((item) => item?.uid))
    }
  }, [locale, ctx])

  return uids
}
